import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  CheckSquare,
  Mail,
  MessageSquare,
  Phone,
  StickyNote,
  Users,
  Zap,
} from 'lucide-react';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Label } from '@/components/ui/Input';
import {
  DateRangeBadges,
  DateRangeInputs,
  dateInputToIso,
  type DateRange,
  type DateRangePreset,
} from '@/components/ui/DateRangePicker';
import { EventFormModal } from '@/components/EventFormModal';
import { ColleagueMultiSelect } from '@/components/ui/ColleagueMultiSelect';
import { useAuthStore } from '@/stores/auth';
import { useRealtimeEvent, REALTIME_EVENTS } from '@/hooks/useRealtimeEvent';
import { displayName } from '@shared/prospect';
import {
  eventsApi,
  EVENT_TYPE_LABELS,
  isTaskOverdue,
  type EventItem,
  type EventType,
} from '@/services/events.api';
import { usersApi, type TeamMember } from '@/services/users.api';
import { cn, truncateAction } from '@/lib/utils';

/**
 * Page Activité - feed de tous les events (tâches, RDV, appels, notes, emails,
 * SMS). Accès manager/direction/admin = scope équipe/tout ; collab = ses
 * events uniquement (enforcé côté serveur).
 *
 * Complémentaire de la page Audit (forensic sécurité) : Activité = lecture
 * commerciale, Audit = lecture mutations BDD.
 */

const TYPE_ICONS: Record<EventType, React.ElementType> = {
  call: Phone,
  meeting: Users,
  task: CheckSquare,
  email: Mail,
  sms: MessageSquare,
  note: StickyNote,
};

const ALL_TYPES: EventType[] = ['task', 'meeting', 'call', 'note', 'email', 'sms'];

type PresetKey = '7d' | '30d' | 'month' | 'all';

// Chaque preset renvoie un couple `{from, to}` en ISO UTC. `to` est toujours
// fin-de-jour (`23:59:59.999Z`) pour inclure la journée courante entièrement.
const ACTIVITY_PRESETS: ReadonlyArray<DateRangePreset<PresetKey>> = [
  {
    key: '7d',
    label: '7 derniers jours',
    range: () => {
      const now = new Date();
      const from = new Date(now.getTime() - 7 * 86_400_000);
      return {
        from: dateInputToIso(from.toISOString().slice(0, 10)),
        to: dateInputToIso(now.toISOString().slice(0, 10), true),
      };
    },
  },
  {
    key: '30d',
    label: '30 derniers jours',
    range: () => {
      const now = new Date();
      const from = new Date(now.getTime() - 30 * 86_400_000);
      return {
        from: dateInputToIso(from.toISOString().slice(0, 10)),
        to: dateInputToIso(now.toISOString().slice(0, 10), true),
      };
    },
  },
  {
    key: 'month',
    label: 'Mois en cours',
    range: () => {
      const now = new Date();
      const y = now.getUTCFullYear();
      const m = now.getUTCMonth();
      return {
        from: new Date(Date.UTC(y, m, 1)).toISOString(),
        to: new Date(Date.UTC(y, m + 1, 0, 23, 59, 59, 999)).toISOString(),
      };
    },
  },
  {
    key: 'all',
    label: "Tout l'historique",
    range: () => {
      const now = new Date();
      // Borne inférieure arbitraire mais lointaine (2020) — suffisant V1.
      return {
        from: '2020-01-01T00:00:00.000Z',
        to: dateInputToIso(now.toISOString().slice(0, 10), true),
      };
    },
  },
];

function initialRange(): DateRange {
  return ACTIVITY_PRESETS[1]!.range(); // '30d' par défaut
}

export function ActivityPage() {
  const token = useAuthStore((s) => s.accessToken)!;
  const user = useAuthStore((s) => s.user);
  const isCollab = user?.role === 'collaborator';

  // Hydratation des filtres depuis l'URL (pour les drill-down externes : clic
  // sur le graph Activité du dashboard → /activity?types=call,meeting,task&from=...&to=...).
  // Les filtres restent modifiables côté UI sans sync URL (local-only après init).
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState<EventItem[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);

  // Filtres — initial state hydraté depuis URL si présent.
  const [userFilter, setUserFilter] = useState<string[]>(() => {
    const u = searchParams.get('user_ids');
    return u ? u.split(',').filter(Boolean) : [];
  });
  const [typeFilter, setTypeFilter] = useState<EventType[]>(() => {
    const t = searchParams.get('types');
    return t ? (t.split(',').filter(Boolean) as EventType[]) : [];
  });
  // Si `?from=&to=` dans l'URL, on utilise ces bornes (range personnalisé,
  // aucun badge actif) ; sinon preset par défaut '30d'.
  const urlFrom = searchParams.get('from');
  const urlTo = searchParams.get('to');
  const [range, setRange] = useState<DateRange>(() =>
    urlFrom && urlTo ? { from: urlFrom, to: urlTo } : initialRange(),
  );
  const [activePreset, setActivePreset] = useState<PresetKey | null>(() =>
    urlFrom && urlTo ? null : '30d',
  );

  // Chargement des collègues (pour multi-select commercial) - sauf collab.
  useEffect(() => {
    if (isCollab) return;
    let cancelled = false;
    usersApi
      .teamMembers(token)
      .then((list) => {
        if (!cancelled) setMembers(list);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, isCollab]);

  const load = useCallback(
    async (opts: { append?: boolean; cursor?: string } = {}) => {
      setError(null);
      if (!opts.append) setItems(null);
      try {
        const res = await eventsApi.activity(token, {
          limit: 50,
          cursor: opts.cursor,
          user_ids: userFilter.length > 0 ? userFilter : undefined,
          types: typeFilter.length > 0 ? typeFilter : undefined,
          from: range.from,
          to: range.to,
        });
        setNextCursor(res.nextCursor);
        setItems((prev) => (opts.append && prev ? [...prev, ...res.data] : res.data));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur');
        if (!opts.append) setItems([]);
      }
    },
    [token, userFilter, typeFilter, range],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Realtime : refresh le feed quand une tâche est mutée. Les events non-task
  // (appels, notes, emails, sms) peuvent aussi être saisis → mais on n'émet
  // pour l'instant que sur les tâches (cf realtime.service.ts). Si besoin on
  // élargira à un event.created générique en V2.
  useRealtimeEvent(
    REALTIME_EVENTS.TASK_MUTATED,
    useCallback(() => {
      void load();
    }, [load]),
  );

  async function handleLoadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      await load({ append: true, cursor: nextCursor });
    } finally {
      setLoadingMore(false);
    }
  }

  function toggleType(t: EventType) {
    setTypeFilter((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  }

  const activeFiltersCount =
    userFilter.length + typeFilter.length + (activePreset !== '30d' ? 1 : 0);

  const visibleMembers = useMemo(() => members, [members]);

  return (
    <AppLayout>
      <PageHeader
        title="Activité"
        subtitle={
          isCollab
            ? 'Historique de vos événements.'
            : 'Historique des événements de votre équipe - tâches, RDV, appels, notes.'
        }
      />

      <Card className="mb-4 p-4">
        {/* Alignement `items-start` : les 3 colonnes (Période, Types, Collab)
            partent du haut. La colonne Période est plus grande (inputs +
            badges + bouton "Tout effacer" empilés), les deux autres s'alignent
            sur son premier rang (inputs). */}
        <div className="flex flex-wrap items-start gap-4">
          <div>
            <Label>Période</Label>
            {/* Inputs date sur la ligne des filtres (avec types, collaborateurs).
                Les badges sont affichés juste en-dessous, sous les 2 inputs. */}
            <DateRangeInputs
              value={range}
              onChange={(next, key) => {
                setRange(next);
                setActivePreset(key);
              }}
            />
            <div className="mt-1.5">
              <DateRangeBadges
                activePresetKey={activePreset}
                presets={ACTIVITY_PRESETS}
                onChange={(next, key) => {
                  setRange(next);
                  setActivePreset(key);
                }}
              />
            </div>
          </div>

          <div className="min-w-[200px] flex-1">
            <Label>Types d&apos;événement</Label>
            <div className="flex flex-wrap gap-1">
              {ALL_TYPES.map((t) => {
                const on = typeFilter.includes(t);
                const Icon = TYPE_ICONS[t];
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleType(t)}
                    className={[
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      on
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-border-subtle text-text-muted hover:border-accent/50',
                    ].join(' ')}
                  >
                    <Icon className="h-3 w-3" />
                    {EVENT_TYPE_LABELS[t]}
                  </button>
                );
              })}
            </div>
          </div>

          {!isCollab && visibleMembers.length > 0 && (
            <div>
              <Label>Collaborateurs</Label>
              <ColleagueMultiSelect
                members={visibleMembers}
                selected={userFilter}
                onChange={setUserFilter}
                placeholder="Tous les collaborateurs"
                headerLabel="Collaborateurs"
                pluralLabel="collaborateurs"
              />
            </div>
          )}

          {activeFiltersCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setUserFilter([]);
                setTypeFilter([]);
                setRange(ACTIVITY_PRESETS[1]!.range()); // '30d'
                setActivePreset('30d');
              }}
            >
              Tout effacer
            </Button>
          )}
        </div>
      </Card>

      {error && (
        <Banner tone="danger" className="mb-4">
          {error}
        </Banner>
      )}

      {items === null && <Skeleton className="h-[400px] w-full" />}

      {items !== null && items.length === 0 && (
        <Card>
          <EmptyState
            title="Aucune activité"
            description="Aucun événement ne correspond à ces filtres sur la période choisie."
          />
        </Card>
      )}

      {items && items.length > 0 && (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
              <tr>
                <th className="px-4 py-2.5 text-left">Date</th>
                <th className="px-4 py-2.5 text-left">Commercial</th>
                <th className="px-4 py-2.5 text-left">Type</th>
                <th className="px-4 py-2.5 text-left">Prospect</th>
                <th className="px-4 py-2.5 text-left">Opp.</th>
                <th className="px-4 py-2.5 text-left">Action</th>
                <th className="px-4 py-2.5 text-left"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((ev) => {
                const Icon = TYPE_ICONS[ev.type];
                const isAuto = Boolean(ev.prospectSequence);
                // Étend la sémantique done/overdue aux meetings task-like
                // (refonte 2026-05-21 Q3). `isTaskOverdue` reconnaît déjà les
                // 2 types ; on miroir ici pour le badge "done".
                const overdue = isTaskOverdue(ev);
                const done =
                  (ev.type === 'task' || ev.type === 'meeting') && ev.taskStatus === 'done';
                return (
                  <tr
                    key={ev.id}
                    className="cursor-pointer border-t border-gray-100 hover:bg-gray-50"
                    onClick={() => setSelectedEvent(ev)}
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-text-muted">
                      {new Date(ev.createdAt).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs">
                      {ev.creator.firstName} {ev.creator.lastName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs">
                      <span className="inline-flex items-center gap-1 text-text-muted">
                        <Icon className="h-3 w-3" />
                        {EVENT_TYPE_LABELS[ev.type]}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs">
                      <Link
                        to={`/prospects/${ev.prospect.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-700 hover:underline"
                      >
                        {truncateAction(displayName(ev.prospect), 30) || ev.prospect.id}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs">
                      {ev.opportunity ? (
                        <Link
                          to={{
                            pathname: `/prospects/${ev.prospect.id}`,
                            search: `?tab=opportunities&opportunity=${ev.opportunity.id}`,
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="font-mono text-[11px] text-blue-700 hover:underline"
                        >
                          {ev.opportunity.reference}
                        </Link>
                      ) : (
                        <span className="text-text-muted">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-brand">
                      {truncateAction(ev.title, 60) || '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        {isAuto && (
                          <span
                            title="Créé par une automatisation / séquence"
                            className="inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-text-muted"
                          >
                            <Zap className="h-2.5 w-2.5" />
                            auto
                          </span>
                        )}
                        {done && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-success-50 px-1.5 py-0.5 text-[10px] font-medium text-success-600">
                            <Check className="h-2.5 w-2.5" />
                            done
                          </span>
                        )}
                        {overdue && (
                          <span
                            className={cn(
                              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                              // Décision produit 2026-05-22 Q1 : tâche en
                              // retard reste rouge, RDV en retard orange.
                              ev.type === 'meeting'
                                ? 'bg-warning-50 text-warning-600'
                                : 'bg-danger-50 text-danger',
                            )}
                          >
                            <AlertTriangle className="h-2.5 w-2.5" />
                            retard
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {nextCursor && (
            <div className="border-t border-border-subtle p-3 text-center">
              <Button variant="secondary" size="sm" onClick={handleLoadMore} loading={loadingMore}>
                Charger 50 événements suivants
              </Button>
            </div>
          )}
        </Card>
      )}

      {selectedEvent && (
        <EventFormModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onChange={() => void load()}
        />
      )}
    </AppLayout>
  );
}
